import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    BLOCK_LABELS,
    BLOCKS_BY_KIND,
    createDocument,
    createEmptyBlock,
    documentBlocks,
    PAGE_LIMITS,
    parseToolDocument,
    serializeToolDocument,
    ToolContentDocument,
    ToolContentKind,
    ToolDetailBlock,
    ToolDetailBlockType,
    tryParseDocument,
} from '@easytools/tool-content';
import desktopAgentIcon from '@easytools-assets/desktop-agent/desktop-agent.svg';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import { useDesktopAgent } from '../../contexts/DesktopAgentContext';
import { SUPPORTED_LANGUAGES, languageLabel } from '../../constants/languages';
import BlockFields from './BlockFields';
import ContentCanvas from './ContentCanvas';
import ContentOutline from './ContentOutline';
import { AppearanceFields, PageSettings, SectionSettings } from './ContentInspector';
import {
    BlockAt, PAGE, Target, addSection, duplicateBlock, flatIndex, fromFlat, insertBlock, insertionPoint, moveBlock, patchSection, removeBlock,
    sectionInsertionPoint, setBlock,
} from './documentOps';
import { HistoryPanel, JsonDialog, PublishDialog } from './ContentDialogs';
import { AskBlock, GeneratePanel, TranslatePanel } from './AgentPanels';
import { useAgentRun } from './useAgentRun';
import { useDocHistory } from './useDocHistory';
import FormatToolbar, { handleFormatShortcut } from './FormatToolbar';
import { MediaPickerProvider } from './MediaLibrary';
import { CONTENT_KINDS, diffBlocks, formatWhen, kindLabel, slotStatus, ToolContentSlot, ToolContentVersion } from './contentModel';
import './ToolContentStudio.scss';

interface Props {
    toolId: number;
    toolStatus: string;
    toolVisibility: string;
    /** Mostrato sotto lo studio quando è selezionata la guida (oggi: le guide PDF). */
    guideExtra?: React.ReactNode;
}

type Selection = { kind: ToolContentKind; language: string };
/** Il pannello a destra: le proprietà di ciò che è selezionato, oppure un'azione in corso. */
type Panel = 'inspect' | 'generate' | 'translate' | 'history';

const REFERENCE_LANGUAGE = 'it';

const errorMessage = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { error?: string } } })?.response?.data?.error
    ?? (error instanceof Error ? error.message : fallback);

const PREVIEW_HINT: Record<ToolContentKind, string> = {
    card: 'Come la vede chi apre il pulsante ? del tool nel portale',
    publicPage: 'Come la vede chi visita la pagina del tool sul sito',
    guide: 'Come viene impaginata nella guida',
};

/** Segni "Nuovo" e "Cambiato" sui blocchi di una bozza generata, rispetto a com'era prima (per posizione nel documento). */
function marksFor(before: ToolDetailBlock[], after: ToolDetailBlock[]): Record<number, 'new' | 'changed'> {
    const marks: Record<number, 'new' | 'changed'> = {};
    let index = 0;
    for (const change of diffBlocks(before, after)) {
        if (change.type === 'removed') continue;
        if (change.type === 'added') marks[index] = 'new';
        if (change.type === 'changed') marks[index] = 'changed';
        index++;
    }
    return marks;
}

type Device = 'desktop' | 'tablet' | 'phone';

const DEVICES: { id: Device; label: string; icon: string }[] = [
    { id: 'desktop', label: 'Computer', icon: 'bi-laptop' },
    { id: 'tablet', label: 'Tablet', icon: 'bi-tablet' },
    { id: 'phone', label: 'Telefono', icon: 'bi-phone' },
];

const SHORTCUTS: [string, string][] = [
    ['Ctrl+S', 'Salva la bozza'],
    ['Ctrl+Z / Ctrl+Y', 'Annulla / ripeti'],
    ['Ctrl+C / X / V', 'Copia, taglia, incolla il blocco'],
    ['Ctrl+D', 'Duplica il blocco'],
    ['Canc', 'Elimina il blocco'],
    ['Alt+↑ / Alt+↓', 'Sposta il blocco'],
    ['Esc', 'Seleziona la sezione, poi la pagina'],
    ['Ctrl+B / I / K', 'Grassetto, corsivo, link nel testo selezionato'],
];

/** Il tasto arriva da un campo dove si scrive: lì valgono i comandi del campo. */
const isTyping = (target: EventTarget | null) =>
    target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

/**
 * Studio dei contenuti del tool: pagina pubblica, scheda nel portale e guida, per lingua.
 * Si lavora sempre su una bozza; online va solo ciò che si pubblica. Il Desktop Agent
 * genera, traduce e riscrive i blocchi. Vedi docs/architecture/TOOL_CONTENT_STUDIO.md.
 */
const Studio: React.FC<Props> = ({ toolId, toolStatus, toolVisibility, guideExtra }) => {
    const { showToast } = useToast();
    const agent = useDesktopAgent();
    const agentRun = useAgentRun(toolId);
    const [slots, setSlots] = useState<ToolContentSlot[]>([]);
    const [selection, setSelection] = useState<Selection>({ kind: 'card', language: REFERENCE_LANGUAGE });
    const [slot, setSlot] = useState<ToolContentSlot | null>(null);
    const [loading, setLoading] = useState(true);
    const { doc, setDoc, resetDoc, replaceDoc, pushPast, undo, redo, canUndo, canRedo } = useDocHistory();
    const [device, setDevice] = useState<Device>('desktop');
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [blockTab, setBlockTab] = useState<'content' | 'style'>('content');
    const [baseline, setBaseline] = useState('');
    const [sourceVersion, setSourceVersion] = useState<number | null>(null);
    const [baselineSource, setBaselineSource] = useState<number | null>(null);
    const [started, setStarted] = useState(false);
    const [target, setTarget] = useState<Target>(PAGE);
    const [panel, setPanel] = useState<Panel>('inspect');
    const [history, setHistory] = useState<ToolContentVersion[] | null>(null);
    const [viewing, setViewing] = useState<{ version: ToolContentVersion; doc: ToolContentDocument } | null>(null);
    const [publishOpen, setPublishOpen] = useState(false);
    const [jsonOpen, setJsonOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [marks, setMarks] = useState<Record<number, 'new' | 'changed'>>({});
    const [generatedNote, setGeneratedNote] = useState<string | null>(null);
    const [translating, setTranslating] = useState<string | null>(null);

    const serialized = serializeToolDocument(doc);
    const blocks = documentBlocks(doc);
    const dirty = serialized !== baseline || sourceVersion !== baselineSource;
    const exists = Boolean(slot?.draft || slot?.published) || started;
    const title = `${kindLabel(selection.kind)} · ${languageLabel(selection.language)}`;
    const working = busy || agentRun.busy;

    const slotOf = useCallback((kind: ToolContentKind, language: string) =>
        slots.find(s => s.kind === kind && s.language === language), [slots]);
    const reference = slotOf(selection.kind, REFERENCE_LANGUAGE);
    const languages = useMemo(() => Array.from(new Set([
        ...SUPPORTED_LANGUAGES.map(l => l.code),
        ...slots.map(s => s.language),
    ])), [slots]);

    const loadSlots = useCallback(async () => {
        setSlots(await apiService.adminGetToolContents<ToolContentSlot>(toolId));
    }, [toolId]);

    const loadSlot = useCallback(async (target: Selection) => {
        setLoading(true);
        try {
            const detail = await apiService.adminGetToolContent<ToolContentSlot>(toolId, target.kind, target.language);
            const current = detail.draft ?? detail.published;
            const parsed = parseToolDocument(current?.content);
            setSlot(detail);
            resetDoc(parsed);
            setBaseline(serializeToolDocument(parsed));
            setSourceVersion(current?.sourceVersion ?? null);
            setBaselineSource(current?.sourceVersion ?? null);
            setStarted(false);
            setTarget(PAGE);
            setViewing(null);
            setHistory(null);
            setPanel(p => (p === 'history' ? 'inspect' : p));
            return parsed;
        } catch (error) {
            showToast('Contenuto non caricato', errorMessage(error, 'Riprova'), 'danger');
            return null;
        } finally {
            setLoading(false);
        }
    }, [toolId, showToast, resetDoc]);

    useEffect(() => {
        void loadSlots().catch(error => showToast('Contenuti non caricati', errorMessage(error, 'Riprova'), 'danger'));
    }, [loadSlots, showToast]);
    useEffect(() => { void loadSlot(selection); setMarks({}); setGeneratedNote(null); }, [loadSlot, selection]);

    useEffect(() => {
        if (panel !== 'history' || history) return;
        apiService.adminGetToolContentHistory<ToolContentVersion>(toolId, selection.kind, selection.language)
            .then(setHistory)
            .catch(() => setHistory([]));
    }, [panel, history, toolId, selection]);

    // Modifiche non salvate: avviso alla chiusura della pagina.
    useEffect(() => {
        if (!dirty) return;
        const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    const confirmLeave = () => !dirty || window.confirm('Ci sono modifiche non salvate in questo contenuto. Vuoi lasciarle?');

    const switchTo = (next: Selection) => {
        if (next.kind === selection.kind && next.language === selection.language) return;
        if (agentRun.busy) { showToast('Attendi', 'Il Desktop Agent sta lavorando su questo contenuto', 'warning'); return; }
        if (!confirmLeave()) return;
        setSelection(next);
    };

    const select = (next: Target) => {
        setTarget(next);
        if (panel !== 'inspect' && !agentRun.busy) setPanel('inspect');
    };

    const updateDoc = (next: ToolContentDocument, selectTarget?: Target) => {
        setDoc(next);
        setMarks({});
        if (selectTarget) select(selectTarget);
    };

    const insert = (at: BlockAt, type: ToolDetailBlockType) => {
        setStarted(true);
        const [next, landed] = insertBlock(doc, at, type);
        updateDoc(next, { kind: 'block', ...landed });
    };

    const startFrom = (source: ToolContentDocument, fromVersion: number | null) => {
        const allowed = new Set<string>(BLOCKS_BY_KIND[selection.kind]);
        setDoc({
            ...structuredClone(source),
            sections: source.sections
                .map(section => ({ ...structuredClone(section), blocks: section.blocks.filter(block => allowed.has(block.type)) }))
                .filter(section => section.blocks.length),
        });
        setSourceVersion(fromVersion);
        setStarted(true);
    };

    const startFromSlot = async (kind: ToolContentKind, language: string, linkToReference: boolean) => {
        try {
            const detail = await apiService.adminGetToolContent<ToolContentSlot>(toolId, kind, language);
            const source = parseToolDocument(detail.published?.content);
            // Dalla scheda si prendono i blocchi, non le impostazioni: la pagina pubblica ha le sue.
            startFrom(kind === selection.kind ? source : { ...source, page: {} }, linkToReference ? detail.published?.version ?? null : null);
        } catch (error) {
            showToast('Copia non riuscita', errorMessage(error, 'Riprova'), 'danger');
        }
    };

    const save = async (): Promise<boolean> => {
        if (!blocks.length) {
            showToast('Bozza non salvata', 'Aggiungi almeno un blocco.', 'warning');
            return false;
        }
        setBusy(true);
        try {
            await apiService.adminSaveToolContentDraft(toolId, selection.kind, selection.language, {
                content: serialized,
                origin: slot?.draft?.origin === 'translation' || (!slot?.draft && sourceVersion !== null && selection.language !== REFERENCE_LANGUAGE)
                    ? 'translation' : slot?.draft?.origin === 'agent' ? 'agent' : 'manual',
                sourceVersion: selection.language === REFERENCE_LANGUAGE ? null : sourceVersion,
            });
            await Promise.all([loadSlot(selection), loadSlots()]);
            showToast('Bozza salvata', title, 'success');
            return true;
        } catch (error) {
            showToast('Bozza non salvata', errorMessage(error, 'Riprova'), 'danger');
            return false;
        } finally {
            setBusy(false);
        }
    };

    // Scorciatoie da tastiera (elenco in SHORTCUTS). Dentro un campo di testo valgono solo
    // salva e formattazione: annulla, copia e incolla restano quelli del campo.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const mod = e.ctrlKey || e.metaKey;
            const key = e.key.toLowerCase();
            if (mod && key === 's') {
                e.preventDefault();
                if (dirty && !working && !viewing) void save();
                return;
            }
            if (!exists || loading || viewing || working || publishOpen || jsonOpen) return;
            if (handleFormatShortcut(e)) return;
            if (isTyping(e.target)) return;

            if (mod && (key === 'z' || key === 'y')) {
                e.preventDefault();
                if (key === 'y' || e.shiftKey) redo(); else undo();
                setMarks({});
                return;
            }
            if (e.key === 'Escape') {
                select(target.kind === 'block' ? { kind: 'section', section: target.section } : PAGE);
                return;
            }
            if (target.kind !== 'block') return;
            const at: BlockAt = target;
            if (mod && key === 'd') {
                e.preventDefault();
                const [next, landed] = duplicateBlock(doc, at);
                updateDoc(next, { kind: 'block', ...landed });
            } else if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                updateDoc(removeBlock(doc, at), { kind: 'section', section: at.section });
            } else if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                e.preventDefault();
                const up = e.key === 'ArrowUp';
                const section = doc.sections[at.section];
                const to = up
                    ? (at.index > 0 ? { section: at.section, index: at.index - 1 } : at.section > 0 ? { section: at.section - 1, index: doc.sections[at.section - 1].blocks.length } : null)
                    : (at.index < section.blocks.length - 1 ? { section: at.section, index: at.index + 2 } : at.section < doc.sections.length - 1 ? { section: at.section + 1, index: 0 } : null);
                if (!to) return;
                const [next, landed] = moveBlock(doc, at, to);
                updateDoc(next, { kind: 'block', ...landed });
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    // Copia, taglia e incolla dei blocchi: il blocco viaggia come JSON negli appunti, così si
    // incolla anche in un altro contenuto o in un'altra lingua (se il tipo è ammesso lì).
    useEffect(() => {
        if (!exists || loading || viewing) return;
        const copy = (e: ClipboardEvent, cut: boolean) => {
            if (isTyping(e.target) || target.kind !== 'block' || !selectedBlock || !e.clipboardData) return;
            e.preventDefault();
            e.clipboardData.setData('text/plain', JSON.stringify(selectedBlock, null, 2));
            if (cut) updateDoc(removeBlock(doc, target), { kind: 'section', section: target.section });
            showToast(cut ? 'Blocco tagliato' : 'Blocco copiato', 'Incollalo con Ctrl+V dove vuoi', 'success');
        };
        const onCopy = (e: ClipboardEvent) => copy(e, false);
        const onCut = (e: ClipboardEvent) => copy(e, true);
        const onPaste = (e: ClipboardEvent) => {
            if (isTyping(e.target)) return;
            const pasted = tryParseDocument(e.clipboardData?.getData('text/plain') ?? '', selection.kind);
            if (!pasted) return;
            e.preventDefault();
            let next = doc;
            let at = insertionPoint(doc, target);
            for (const block of documentBlocks(pasted)) {
                const [withBlock, landed] = insertBlock(next, at, block.type);
                next = setBlock(withBlock, landed, structuredClone(block));
                at = { section: landed.section, index: landed.index + 1 };
            }
            setStarted(true);
            updateDoc(next, { kind: 'block', section: at.section, index: at.index - 1 });
        };
        document.addEventListener('copy', onCopy);
        document.addEventListener('cut', onCut);
        document.addEventListener('paste', onPaste);
        return () => {
            document.removeEventListener('copy', onCopy);
            document.removeEventListener('cut', onCut);
            document.removeEventListener('paste', onPaste);
        };
    });

    const openPublish = async () => {
        if (dirty && !(await save())) return;
        setPublishOpen(true);
    };

    const publish = async () => {
        setBusy(true);
        try {
            const published = await apiService.adminPublishToolContent<ToolContentVersion>(toolId, selection.kind, selection.language);
            setPublishOpen(false);
            setGeneratedNote(null);
            setMarks({});
            await Promise.all([loadSlot(selection), loadSlots()]);
            showToast('Pubblicato', `${title} · versione ${published.version}`, 'success');
        } catch (error) {
            showToast('Pubblicazione non riuscita', errorMessage(error, 'Riprova'), 'danger');
        } finally {
            setBusy(false);
        }
    };

    const discard = async () => {
        setMoreOpen(false);
        if (!window.confirm('Scartare la bozza? Le modifiche non pubblicate andranno perse.')) return;
        setBusy(true);
        try {
            await apiService.adminDiscardToolContentDraft(toolId, selection.kind, selection.language);
            await Promise.all([loadSlot(selection), loadSlots()]);
            setGeneratedNote(null);
            showToast('Bozza scartata', title, 'success');
        } catch (error) {
            showToast('Bozza non scartata', errorMessage(error, 'Riprova'), 'danger');
        } finally {
            setBusy(false);
        }
    };

    const discardLocal = () => {
        setMoreOpen(false);
        if (!window.confirm('Annullare le modifiche non salvate?')) return;
        void loadSlot(selection);
    };

    const view = async (version: ToolContentVersion) => {
        try {
            const full = await apiService.adminGetToolContentVersion<ToolContentVersion>(toolId, selection.kind, selection.language, version.version);
            setViewing({ version: full, doc: parseToolDocument(full.content) });
            setTarget(PAGE);
        } catch (error) {
            showToast('Versione non caricata', errorMessage(error, 'Riprova'), 'danger');
        }
    };

    const restore = async (version: ToolContentVersion) => {
        if (!confirmLeave()) return;
        setBusy(true);
        try {
            try {
                await apiService.adminRestoreToolContent(toolId, selection.kind, selection.language, version.version);
            } catch (error) {
                if ((error as { response?: { status?: number } })?.response?.status !== 409) throw error;
                if (!window.confirm(`Esiste già una bozza. Vuoi sostituirla con la versione ${version.version}?`)) return;
                await apiService.adminRestoreToolContent(toolId, selection.kind, selection.language, version.version, true);
            }
            await Promise.all([loadSlot(selection), loadSlots()]);
            showToast('Versione ripristinata in bozza', `${title} · dalla versione ${version.version}`, 'success');
        } catch (error) {
            showToast('Ripristino non riuscito', errorMessage(error, 'Riprova'), 'danger');
        } finally {
            setBusy(false);
        }
    };

    // ── Desktop Agent ───────────────────────────────────────────
    const generate = async (mode: 'improve' | 'rewrite') => {
        const previous = doc;
        const before = slot?.published ? documentBlocks(parseToolDocument(slot.published.content)) : blocks;
        const prompt = doc.page.agentNotes?.trim() ?? '';
        try {
            await agentRun.run({ kind: selection.kind, language: selection.language, mode, instructions: prompt || undefined });
            const after = await loadSlot(selection);
            await loadSlots();
            if (after) {
                setMarks(marksFor(before, documentBlocks(after)));
                // Il prompt usato resta quello dell'editor, anche se non era ancora salvato.
                if (prompt !== (after.page.agentNotes ?? '').trim()) replaceDoc({ ...after, page: { ...after.page, agentNotes: prompt || undefined } });
                // Ctrl+Z riporta a com'era prima della generazione.
                pushPast(previous);
            }
            const count = after ? documentBlocks(after).length : 0;
            setGeneratedNote(`Il Desktop Agent ha scritto ${after?.sections.length ?? 0} sezioni con ${count} blocchi. Quelli nuovi o cambiati sono segnati: rivedili, poi pubblica. Ctrl+Z torna alla versione di prima.`);
            setPanel('inspect');
        } catch (error) {
            const message = errorMessage(error, 'Riprova');
            showToast(message === 'Annullato' ? 'Generazione annullata' : 'Generazione non riuscita',
                message === 'Annullato' ? 'Nessuna modifica alla bozza' : message, message === 'Annullato' ? 'warning' : 'danger');
        }
    };

    const translate = async (targets: string[]) => {
        const done: string[] = [];
        for (const language of targets) {
            setTranslating(language);
            try {
                await agentRun.run({ kind: selection.kind, language, mode: 'translate' });
                done.push(languageLabel(language));
            } catch (error) {
                const message = errorMessage(error, 'Riprova');
                showToast(`Traduzione in ${languageLabel(language)} non riuscita`, message, message === 'Annullato' ? 'warning' : 'danger');
                if (message === 'Annullato') break;
            }
        }
        setTranslating(null);
        await loadSlots();
        if (done.length) {
            showToast('Traduzioni pronte in bozza', done.join(', '), 'success');
            setPanel('inspect');
            if (targets.includes(selection.language)) await loadSlot(selection);
        }
    };

    const rewriteBlock = async (request: string) => {
        if (target.kind !== 'block') return;
        const at: BlockAt = { section: target.section, index: target.index };
        const flat = flatIndex(doc, at);
        try {
            const result = await agentRun.run({
                kind: selection.kind, language: selection.language, mode: 'block',
                instructions: request, blockIndex: flat, blocks: JSON.stringify(blocks),
            });
            if (!result.block) throw new Error('Il Desktop Agent non ha restituito il blocco.');
            const block = JSON.parse(result.block) as ToolDetailBlock;
            setDoc(prev => (fromFlat(prev, flat) ? setBlock(prev, at, block) : prev));
            setMarks({ [flat]: 'changed' });
            showToast('Blocco riscritto', 'Rivedilo e salva la bozza', 'success');
        } catch (error) {
            const message = errorMessage(error, 'Riprova');
            if (message !== 'Annullato') showToast('Blocco non riscritto', message, 'danger');
        }
    };

    // ── Testi di stato ──────────────────────────────────────────
    const notice = (() => {
        if (selection.kind === 'publicPage') {
            const hidden = toolStatus !== 'Active' || toolVisibility !== 'Public';
            return hidden
                ? 'Il tool non è Attivo e Pubblico: anche una volta pubblicata, questa pagina resterà nascosta sul sito.'
                : 'Il sito non mostra ancora le pagine dei tool: compariranno quando sarà attiva la sezione /strumenti.';
        }
        if (selection.kind === 'guide')
            return 'Il PDF scaricabile dal portale si carica ancora in fondo alla pagina: la guida a blocchi lo sostituirà.';
        return null;
    })();
    const publishWarning = selection.kind === 'publicPage' && (toolStatus !== 'Active' || toolVisibility !== 'Public')
        ? 'Il tool non è Attivo e Pubblico: la pagina verrà pubblicata ma il sito non la mostrerà finché il tool resta così.'
        : undefined;

    const statusLine = [
        slot?.draft && `Bozza v${slot.draft.version}, salvata ${formatWhen(slot.draft.updatedAt)}`,
        slot?.published && `online la v${slot.published.version} dal ${formatWhen(slot.published.publishedAt)}`,
        !slot?.draft && !slot?.published && (started ? 'Nuovo contenuto, non ancora salvato' : 'Non ancora creato in questa lingua'),
    ].filter(Boolean).join(' · ');

    const outdated = slot?.isOutdated && selection.language !== REFERENCE_LANGUAGE;
    const referenceVersion = reference?.published?.version ?? null;
    const draftVersion = slot?.draft?.version ?? ((slot?.published?.version ?? 0) + 1);
    const canPublish = dirty || Boolean(slot?.draft);
    const agentReady = agent.state === 'ready';
    const selectedBlock = target.kind === 'block' ? doc.sections[target.section]?.blocks[target.index] : undefined;
    const selectedSection = target.kind === 'section' ? doc.sections[target.section] : undefined;
    // Struttura e libreria servono solo mentre si modifica la bozza.
    const editing = exists && !loading && !viewing;

    const kindDot = (kind: ToolContentKind) => {
        const tones = slots.filter(s => s.kind === kind).map(s => slotStatus(s).tone);
        return tones.includes('outdated') ? 'outdated' : tones.includes('draft') ? 'draft' : tones.includes('published') ? 'published' : 'none';
    };

    return (
        <div className="tcs">
            <section className="tcs-switch" aria-label="Contenuto da modificare">
                <div className="tcs-switch__kinds" role="group" aria-label="Contenuto">
                    {CONTENT_KINDS.map(k => (
                        <button key={k.kind} type="button" className={`tcs-switch__kind${selection.kind === k.kind ? ' is-on' : ''}`}
                            aria-pressed={selection.kind === k.kind} title={k.hint} onClick={() => switchTo({ kind: k.kind, language: selection.language })}>
                            <span className={`tcs-dot tcs-dot--${kindDot(k.kind)}`} aria-hidden="true" />{k.label}
                        </button>
                    ))}
                </div>
                <div className="tcs-switch__langs" role="group" aria-label="Lingua">
                    {languages.map(language => {
                        const status = slotStatus(slotOf(selection.kind, language));
                        const on = selection.language === language;
                        return (
                            <button key={language} type="button" className={`tcs-switch__lang${on ? ' is-on' : ''}`} aria-pressed={on}
                                aria-label={`${languageLabel(language)}: ${status.label}`} title={`${languageLabel(language)}: ${status.label}`}
                                onClick={() => switchTo({ kind: selection.kind, language })}>
                                <span className={`tcs-dot tcs-dot--${status.tone}`} aria-hidden="true" />{language.toUpperCase()}
                            </button>
                        );
                    })}
                </div>
                <p className="tcs-switch__status">
                    {statusLine}
                    {dirty && <strong className="tcs-switch__dirty"> · Modifiche non salvate</strong>}
                </p>
                {!viewing && (
                    <div className="tcs-switch__actions">
                        <button type="button" className={`tcs-agent-btn${agentReady ? ' is-ready' : ''}`} disabled={working}
                            onClick={() => { setPanel('generate'); }}>
                            <img src={desktopAgentIcon} alt="" aria-hidden="true" />Genera
                        </button>
                        {selection.language !== REFERENCE_LANGUAGE || referenceVersion ? (
                            <button type="button" className="btn btn-sm btn-light tcs-switch__btn" disabled={working}
                                onClick={() => { setPanel('translate'); }}>Traduci</button>
                        ) : null}
                        {dirty && <button type="button" className="btn btn-sm btn-outline-primary tcs-switch__btn" disabled={working} onClick={() => void save()} title="Ctrl+S">Salva bozza</button>}
                        <button type="button" className="btn btn-sm btn-primary tcs-switch__btn" disabled={working || !canPublish} onClick={() => void openPublish()}>
                            {canPublish ? 'Pubblica…' : 'Pubblicato'}
                        </button>
                        <div className="tcs-more">
                            <button type="button" className="btn btn-sm btn-light tcs-switch__btn" aria-haspopup="menu" aria-expanded={moreOpen}
                                aria-label="Altre azioni" onClick={() => setMoreOpen(o => !o)}><i className="bi bi-three-dots" aria-hidden="true" /></button>
                            {moreOpen && (
                                <div className="tcs-more__menu" role="menu" onMouseLeave={() => setMoreOpen(false)}>
                                    <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setPanel('history'); }}>Storico delle versioni</button>
                                    <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setJsonOpen(true); }}>Vedi il JSON</button>
                                    {dirty && <button type="button" role="menuitem" onClick={discardLocal}>Annulla le modifiche</button>}
                                    {slot?.draft && !dirty && <button type="button" role="menuitem" className="is-danger" onClick={() => void discard()}>Scarta la bozza</button>}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>

            {generatedNote && !viewing && (
                <div className="tcs-banner tcs-banner--agent" role="status">
                    <img src={desktopAgentIcon} alt="" aria-hidden="true" />
                    <span><strong>Bozza generata dal Desktop Agent.</strong> {generatedNote}</span>
                    <button type="button" className="btn btn-sm btn-light" onClick={() => { setGeneratedNote(null); setMarks({}); }}>Ho capito</button>
                </div>
            )}

            {outdated && !viewing && (
                <div className="tcs-banner tcs-banner--outdated" role="status">
                    <span>
                        L'italiano è cambiato dopo questa traduzione{referenceVersion ? ` (ora è alla versione ${referenceVersion})` : ''}.
                        Traducilo di nuovo, oppure aggiorna i blocchi e segna la traduzione come allineata.
                    </span>
                    <span className="tcs-banner__actions">
                        <button type="button" className="btn btn-sm btn-light" disabled={working} onClick={() => setPanel('translate')}>Traduci di nuovo</button>
                        {referenceVersion && sourceVersion !== referenceVersion && (
                            <button type="button" className="btn btn-sm btn-light" onClick={() => setSourceVersion(referenceVersion)}>Segna come allineata</button>
                        )}
                    </span>
                </div>
            )}

            {viewing && (
                <div className="tcs-banner" role="status">
                    <span>Stai guardando la versione {viewing.version.version} ({viewing.version.state === 'published' ? 'pubblicata' : 'archiviata'}). Non si modifica qui.</span>
                    <span className="tcs-banner__actions">
                        <button type="button" className="btn btn-sm btn-light" onClick={() => void restore(viewing.version)}>Ripristina in bozza</button>
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => setViewing(null)}>Torna alla bozza</button>
                    </span>
                </div>
            )}

            {notice && <p className="tcs-notice">{notice}</p>}

            <div className={`tcs-body${editing ? ' has-outline' : ''}`}>
                {editing && (
                    <aside className="tcs-left" aria-label="Struttura e blocchi">
                        <ContentOutline kind={selection.kind} doc={doc} target={target} onSelect={select} onChange={updateDoc}
                            onAdd={type => insert(insertionPoint(doc, target), type)}
                            onAddSection={template => {
                                const at = sectionInsertionPoint(doc, target);
                                setStarted(true);
                                updateDoc(addSection(doc, at, template.build()), { kind: 'section', section: at });
                            }} />
                    </aside>
                )}

                <div className={`tcs-stage tcs-stage--${editing ? device : 'desktop'}`}>
                    {exists && !loading && (
                        <div className="tcs-stage__bar">
                            <p className="tcs-stage__hint">{PREVIEW_HINT[selection.kind]}</p>
                            {editing && (
                                <div className="tcs-stage__tools">
                                    <button type="button" className="tcs-tool" aria-label="Annulla" title="Annulla (Ctrl+Z)" disabled={!canUndo}
                                        onClick={() => { undo(); setMarks({}); }}><i className="bi bi-arrow-counterclockwise" aria-hidden="true" /></button>
                                    <button type="button" className="tcs-tool" aria-label="Ripeti" title="Ripeti (Ctrl+Y)" disabled={!canRedo}
                                        onClick={() => { redo(); setMarks({}); }}><i className="bi bi-arrow-clockwise" aria-hidden="true" /></button>
                                    <span className="tcs-stage__sep" aria-hidden="true" />
                                    <div className="tcs-devices" role="radiogroup" aria-label="Anteprima per dispositivo">
                                        {DEVICES.map(d => (
                                            <button key={d.id} type="button" role="radio" aria-checked={device === d.id} aria-label={d.label} title={d.label}
                                                className={`tcs-tool${device === d.id ? ' is-on' : ''}`} onClick={() => setDevice(d.id)}>
                                                <i className={`bi ${d.icon}`} aria-hidden="true" />
                                            </button>
                                        ))}
                                    </div>
                                    <span className="tcs-stage__sep" aria-hidden="true" />
                                    <div className="tcs-shortcuts">
                                        <button type="button" className="tcs-tool" aria-label="Scorciatoie da tastiera" title="Scorciatoie da tastiera"
                                            aria-expanded={shortcutsOpen} onClick={() => setShortcutsOpen(o => !o)}><i className="bi bi-keyboard" aria-hidden="true" /></button>
                                        {shortcutsOpen && (
                                            <div className="tcs-shortcuts__panel" role="dialog" aria-label="Scorciatoie da tastiera" onMouseLeave={() => setShortcutsOpen(false)}>
                                                <dl>
                                                    {SHORTCUTS.map(([keys, what]) => <React.Fragment key={keys}><dt><kbd>{keys}</kbd></dt><dd>{what}</dd></React.Fragment>)}
                                                </dl>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {editing && <FormatToolbar />}
                    {loading ? (
                        <p className="tcs-stage__loading"><span className="spinner-border spinner-border-sm me-2" />Caricamento…</p>
                    ) : viewing ? (
                        <ContentCanvas kind={selection.kind} doc={viewing.doc} target={null} readOnly
                            onSelect={() => undefined} onChange={() => undefined} onInsert={() => undefined} />
                    ) : !exists ? (
                        <EmptyState
                            title={title}
                            language={selection.language}
                            reference={selection.language !== REFERENCE_LANGUAGE ? reference?.published ?? null : null}
                            card={selection.kind !== 'card' ? slotOf('card', selection.language)?.published ?? null : null}
                            agentReady={agentReady}
                            onGenerate={() => setPanel('generate')}
                            onTranslate={() => setPanel('translate')}
                            onBlank={() => startFrom(createDocument([createEmptyBlock('hero')]), null)}
                            onCopyReference={() => void startFromSlot(selection.kind, REFERENCE_LANGUAGE, true)}
                            onFromCard={() => void startFromSlot('card', selection.language, false)}
                        />
                    ) : (
                        <ContentCanvas kind={selection.kind} doc={doc} target={panel === 'inspect' ? target : null} marks={marks}
                            onSelect={select} onChange={updateDoc} onInsert={insert} />
                    )}
                </div>

                <aside className="tcs-side" aria-label="Proprietà">
                    {panel === 'inspect' && !viewing && (
                        <>
                            <div className="tcs-side__head">
                                <h3 className="tcs-side__title">
                                    {selectedBlock ? BLOCK_LABELS[selectedBlock.type]
                                        : selectedSection && target.kind === 'section' ? `Sezione ${target.section + 1}`
                                            : 'Pagina'}
                                </h3>
                                {target.kind !== 'page' && (
                                    <button type="button" className="btn btn-sm btn-light" onClick={() => select(PAGE)}>
                                        <i className="bi bi-arrow-left me-1" aria-hidden="true" />Pagina
                                    </button>
                                )}
                            </div>
                            <div className="tcs-side__body">
                                {selectedBlock && target.kind === 'block' ? (
                                    <div className="tcs-fields">
                                        <div className="tcs-choice__options tcs-side__tabs" role="tablist" aria-label="Proprietà del blocco">
                                            <button type="button" role="tab" aria-selected={blockTab === 'content'} className={`tcs-choice__option${blockTab === 'content' ? ' is-on' : ''}`} onClick={() => setBlockTab('content')}>Contenuto</button>
                                            <button type="button" role="tab" aria-selected={blockTab === 'style'} className={`tcs-choice__option${blockTab === 'style' ? ' is-on' : ''}`} onClick={() => setBlockTab('style')}>
                                                Aspetto{selectedBlock.appearance && <span className="tcs-side__tab-dot" aria-label="(personalizzato)" />}
                                            </button>
                                        </div>
                                        {blockTab === 'style' ? (
                                            <AppearanceFields appearance={selectedBlock.appearance}
                                                onChange={appearance => setDoc(prev => {
                                                    const { appearance: _old, ...rest } = prev.sections[target.section].blocks[target.index];
                                                    return setBlock(prev, target, (appearance ? { ...rest, appearance } : rest) as ToolDetailBlock);
                                                }, `block:${target.section}:${target.index}:appearance`)} />
                                        ) : <>
                                        <p className="tcs-fields__note">I testi si scrivono nell'anteprima; qui il resto del blocco.</p>
                                        {agentRun.busy
                                            ? <p className="tcs-fields__note"><span className="spinner-border spinner-border-sm me-2" />Il Desktop Agent sta riscrivendo il blocco… <button type="button" className="btn btn-link btn-sm p-0" onClick={agentRun.cancel}>Annulla</button></p>
                                            : <AskBlock busy={working} onAsk={request => void rewriteBlock(request)} />}
                                        <BlockFields
                                            key={`${target.section}-${target.index}-${JSON.stringify(marks)}`}
                                            block={selectedBlock}
                                            onPatch={patch => setDoc(prev => setBlock(prev, target, { ...prev.sections[target.section].blocks[target.index], ...patch } as ToolDetailBlock),
                                                `block:${target.section}:${target.index}:${Object.keys(patch).join()}`)}
                                        />
                                        </>}
                                        <div className="tcs-side__actions">
                                            <button type="button" className="btn btn-sm btn-light" onClick={() => { const [next, at] = duplicateBlock(doc, target); updateDoc(next, { kind: 'block', ...at }); }}>
                                                <i className="bi bi-copy me-1" aria-hidden="true" />Duplica
                                            </button>
                                            <button type="button" className="btn btn-sm btn-light" disabled={target.section === 0}
                                                onClick={() => { const [next, at] = moveBlock(doc, target, { section: target.section - 1, index: doc.sections[target.section - 1].blocks.length }); updateDoc(next, { kind: 'block', ...at }); }}>
                                                Alla sezione sopra
                                            </button>
                                            <button type="button" className="btn btn-sm btn-light" disabled={target.section === doc.sections.length - 1}
                                                onClick={() => { const [next, at] = moveBlock(doc, target, { section: target.section + 1, index: 0 }); updateDoc(next, { kind: 'block', ...at }); }}>
                                                Alla sezione sotto
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => updateDoc(removeBlock(doc, target), { kind: 'section', section: target.section })}>
                                                <i className="bi bi-trash me-1" aria-hidden="true" />Elimina
                                            </button>
                                        </div>
                                    </div>
                                ) : selectedSection && target.kind === 'section' ? (
                                    <SectionSettings section={selectedSection} onChange={patch => setDoc(prev => patchSection(prev, target.section, patch))} />
                                ) : (
                                    <PageSettings kind={selection.kind} page={doc.page} sections={doc.sections.filter(s => s.blocks.length).length} blocks={blocks.length}
                                        onChange={(page, field) => setDoc(prev => ({ ...prev, page }), `page:${field}`)}
                                        onOpenPrompt={() => setPanel('generate')} />
                                )}
                            </div>
                        </>
                    )}

                    {panel === 'inspect' && viewing && (
                        <div className="tcs-side__body">
                            <p className="tcs-fields__note">Versione {viewing.version.version} in sola lettura. Torna alla bozza per modificare.</p>
                        </div>
                    )}

                    {panel === 'generate' && (
                        <>
                            <div className="tcs-side__head">
                                <h3 className="tcs-side__title">Genera con il Desktop Agent</h3>
                                {!agentRun.busy && <button type="button" className="btn btn-sm btn-light" aria-label="Chiudi il pannello" onClick={() => setPanel('inspect')}><i className="bi bi-x-lg" /></button>}
                            </div>
                            <div className="tcs-side__body">
                                <GeneratePanel hasContent={exists && blocks.length > 0} title={title} phase={agentRun.phase} startedAt={agentRun.startedAt}
                                    lastLine={agentRun.lastLine} prompt={doc.page.agentNotes ?? ''} maxLength={PAGE_LIMITS.agentNotes}
                                    onPromptChange={prompt => setDoc(prev => ({ ...prev, page: { ...prev.page, agentNotes: prompt } }), 'page:agentNotes')}
                                    onGenerate={mode => void generate(mode)} onCancel={agentRun.cancel} />
                            </div>
                        </>
                    )}

                    {panel === 'translate' && (
                        <>
                            <div className="tcs-side__head">
                                <h3 className="tcs-side__title">Traduci dalla versione italiana</h3>
                                {!agentRun.busy && <button type="button" className="btn btn-sm btn-light" aria-label="Chiudi il pannello" onClick={() => setPanel('inspect')}><i className="bi bi-x-lg" /></button>}
                            </div>
                            <div className="tcs-side__body">
                                <TranslatePanel slots={slots.filter(s => s.kind === selection.kind)} languages={languages} referenceVersion={referenceVersion}
                                    phase={agentRun.phase} startedAt={agentRun.startedAt} lastLine={agentRun.lastLine} current={translating}
                                    onTranslate={targets => void translate(targets)} onCancel={agentRun.cancel} />
                            </div>
                        </>
                    )}

                    {panel === 'history' && (
                        <HistoryPanel versions={history} viewing={viewing?.version.version ?? null}
                            onView={v => void view(v)} onRestore={v => void restore(v)} onClose={() => { setPanel('inspect'); setViewing(null); }} />
                    )}
                </aside>
            </div>

            {selection.kind === 'guide' && guideExtra}

            <PublishDialog
                show={publishOpen}
                title={`${kindLabel(selection.kind).toLowerCase()} · ${languageLabel(selection.language)}`}
                version={draftVersion}
                published={slot?.published ? parseToolDocument(slot.published.content) : null}
                draft={doc}
                warning={publishWarning}
                busy={busy}
                onConfirm={() => void publish()}
                onClose={() => setPublishOpen(false)}
            />
            <JsonDialog show={jsonOpen} kind={selection.kind} doc={viewing?.doc ?? doc} readOnly={Boolean(viewing)}
                onReplace={next => { setStarted(true); updateDoc(next, PAGE); }} onClose={() => setJsonOpen(false)} />
        </div>
    );
};

const EmptyState: React.FC<{
    title: string;
    language: string;
    reference: ToolContentVersion | null;
    card: ToolContentVersion | null;
    agentReady: boolean;
    onGenerate: () => void;
    onTranslate: () => void;
    onBlank: () => void;
    onCopyReference: () => void;
    onFromCard: () => void;
}> = ({ title, language, reference, card, agentReady, onGenerate, onTranslate, onBlank, onCopyReference, onFromCard }) => (
    <div className="tcs-empty">
        <h3>{title}: non ancora creata</h3>
        <p>
            {language === REFERENCE_LANGUAGE
                ? 'Scrivi un prompt e il Desktop Agent prepara una bozza da rivedere. Puoi anche partire da un documento vuoto.'
                : reference
                    ? 'Parti dalla versione italiana: il Desktop Agent la traduce e la lascia in bozza da rivedere.'
                    : 'La versione italiana non è ancora pubblicata: puoi partire da un documento vuoto.'}
        </p>
        <div className="tcs-empty__actions">
            {language === REFERENCE_LANGUAGE && <button type="button" className="btn btn-primary" onClick={onGenerate} title={agentReady ? undefined : 'Serve il Desktop Agent pronto'}>Genera una bozza</button>}
            {language !== REFERENCE_LANGUAGE && reference && <button type="button" className="btn btn-primary" onClick={onTranslate}>Traduci dall’italiano</button>}
            {language !== REFERENCE_LANGUAGE && reference && <button type="button" className="btn btn-light" onClick={onCopyReference}>Copia l’italiano (v{reference.version})</button>}
            {card && <button type="button" className="btn btn-light" onClick={onFromCard}>Parti dalla scheda nel portale</button>}
            <button type="button" className="btn btn-light" onClick={onBlank}>Inizia da un documento vuoto</button>
        </div>
    </div>
);

/** Lo studio con la libreria immagini a disposizione di tutti i campi immagine. */
const ToolContentStudio: React.FC<Props> = props => (
    <MediaPickerProvider toolId={props.toolId}>
        <Studio {...props} />
    </MediaPickerProvider>
);

export default ToolContentStudio;
